import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import PageWrapper from '@/components/PageWrapper';
import { FadeUp, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const FAQPage = () => {
  const faqs = [
    {
      question: 'Do I need Pilates experience?',
      answer: 'No. Sessions are suitable for complete beginners through to advanced clients. Everything is tailored to your experience and needs.',
    },
    {
      question: 'What if I have pain or an injury?',
      answer: "Pilates can be adapted to support many injuries and movement limitations. Please let me know about any concerns before your session so we can work safely and effectively.",
    },
    {
      question: 'How often should I attend?',
      answer: 'Most clients benefit from 1-2 sessions per week, depending on goals, availability, and training background.',
    },
    {
      question: 'What should I wear?',
      answer: 'Comfortable clothing you can move freely in. Pilates Grip Socks are mandatory for safety and hygiene reasons.',
    },
    {
      question: 'What is your cancellation policy?',
      answer: "A minimum of 24 hours' notice is required for cancellations. Sessions cancelled with less than 24 hours' notice will be charged in full.",
    },
    {
      question: 'Will I receive reminders?',
      answer: "You'll receive an automatic reminder email the day before your session.",
    },
    {
      question: 'Where is the studio located?',
      answer: 'The studio is located in Kingsland, Auckland. Full address details are provided after booking.',
    },
    {
      question: 'Is parking available?',
      answer: "Free two-hour street parking available. You're welcome to park on the driveway ramp if street parking is full.",
    },
  ];

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="faq-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto">
              <HelpCircle className="mx-auto mb-4 text-stone-400" size={32} strokeWidth={1} />
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Help</p>
              <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                Frequently Asked <span className="italic">Questions</span>
              </h1>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section data-testid="faq-list" className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <StaggerContainer>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <StaggerItem key={index}>
                  <AccordionItem
                    value={`item-${index}`}
                    data-testid={`faq-item-${index}`}
                    className="bg-white border border-stone-200 px-6"
                  >
                    <AccordionTrigger className="text-left font-heading text-xl text-stone-900 hover:no-underline py-6 [&[data-state=open]]:text-stone-600">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-stone-600 leading-relaxed font-light pb-6">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </StaggerItem>
              ))}
            </Accordion>
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <h2 className="font-heading text-4xl text-stone-900">Still Have Questions?</h2>
            <p className="mt-4 text-stone-600 font-light">Get in touch and I'll be happy to help.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button asChild data-testid="faq-cta-contact" className="btn-primary">
                <Link to="/contact">
                  Contact Me <ArrowRight className="ml-2" size={14} strokeWidth={1.5} />
                </Link>
              </Button>
              <Button asChild variant="outline" data-testid="faq-cta-book" className="btn-secondary">
                <Link to="/bookings">Book a Session</Link>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default FAQPage;
